import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ORDER_SORT_KEYS, OrderStatus, type AdminOrderTargetStatus, type Order, type OrderSortKey } from "@/api";
import { orderKeys, useOrderPage, useOrderReceipt, useOrderShipments, useOrderShops, useOrderStatusCounts } from "@/api/hooks/use-orders";
import { useSellers } from "@/api/hooks/use-sellers";
import { shopRoute, useStorefronts } from "@/api/hooks/use-storefronts";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { OrderPaymentRefunds } from "@/components/payment-refund-panel";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store/auth";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { FilterChips, type FilterChip } from "@/components/filter-chips";
import { StackedThumbs } from "@/components/stacked-thumbs";
import { Select } from "@/components/ui/select";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { serverDataTable, useUrlTable } from "@/lib/use-url-table";
import { orderNo } from "@/lib/short-numbers";
import { formatDateTime, formatPrice } from "@/lib/utils";

/**
 * Sixteen order statuses is a correct state machine and a useless filter: the
 * founder does not think in "ready_to_ship vs shipped", she thinks "что в
 * работе" and "где проблемы". Chips group the machine into the five buckets an
 * operator actually triages by; the status dropdown next to them still reaches
 * any single state when someone needs it.
 */
const ORDER_GROUPS = {
  incoming: ["pending", "awaiting_payment", "new", "paid", "confirmed"],
  inWork: ["processing", "in_work", "ready_to_ship"],
  shipping: ["shipped", "in_transit", "awaiting_pickup", "delivered"],
  closed: ["completed", "archived"],
  problem: ["declined", "cancelled", "return_requested", "dispute_open"]
} as const satisfies Record<string, readonly OrderStatus[]>;
type OrderGroup = keyof typeof ORDER_GROUPS;
const GROUP_TONE: Record<OrderGroup, FilterChip<OrderGroup>["tone"]> = {
  incoming: "default",
  inWork: "default",
  shipping: "default",
  closed: "default",
  problem: "danger"
};
const STATUS_VARIANT: Record<OrderStatus, "default" | "secondary" | "destructive"> = {
  pending: "secondary",
  awaiting_payment: "secondary",
  declined: "destructive",
  paid: "default",
  processing: "secondary",
  new: "secondary",
  confirmed: "secondary",
  in_work: "secondary",
  ready_to_ship: "secondary",
  awaiting_pickup: "default",
  shipped: "default",
  in_transit: "default",
  delivered: "default",
  completed: "default",
  cancelled: "destructive",
  return_requested: "secondary",
  dispute_open: "destructive",
  archived: "secondary"
};
const FILTER_KEYS = ["search", "group", "status", "shopId", "from", "to"] as const;

/**
 * Заказы консоли. Фильтр, поиск, сортировка и страница — на сервере
 * (GET /admin/orders?page=…), срез — в адресной строке. Раньше раздел забирал
 * все заказы за всю историю одним ответом (5 000 заказов — 5,8 МБ) и искал по
 * ним в браузере.
 */
export function OrdersPage() {
  const {
    t
  } = useTranslation();
  const table = useUrlTable<OrderSortKey>({
    filters: FILTER_KEYS,
    defaultSort: {
      key: "date",
      dir: "desc"
    },
    defaultPageSize: 25,
    sortKeys: ORDER_SORT_KEYS
  });
  const f = table.filters;
  const group = (f.group && f.group in ORDER_GROUPS ? f.group : "") as OrderGroup | "";
  const status = (OrderStatus.options as string[]).includes(f.status ?? "") ? f.status as OrderStatus : "";
  const query = {
    search: f.search || undefined,
    from: f.from || undefined,
    to: f.to || undefined,
    shopId: f.shopId || undefined
  };
  const statuses: string[] | undefined = status ? [status] : group ? [...ORDER_GROUPS[group]] : undefined;
  const pageQuery = useOrderPage({
    ...query,
    statuses,
    sort: table.sort.key,
    dir: table.sort.dir,
    page: table.page,
    pageSize: table.pageSize
  });
  const counts = useOrderStatusCounts(query).data ?? {};
  // Магазины, встречающиеся в заказах, — для фильтра «Магазин» (с сервера:
  // по одной странице список магазинов был бы неполным).
  const shopOptions = useOrderShops().data ?? [];
  const rows = pageQuery.data?.items ?? [];
  const total = pageQuery.data?.total ?? 0;
  const view = serverDataTable(table, rows, total);
  const error = pageQuery.error ? pageQuery.error.message : null;
  // Храним id, а не снимок: после смены статуса заказ перечитывается по
  // номеру, и открытая карточка показывает новый статус и историю. Заказ
  // может быть на любой странице, поэтому берём его с сервера по номеру.
  const [params, setParams] = useSearchParams();
  const deepLinked = params.get("order");
  const [selectedId, setSelectedId] = useState<string | null>(deepLinked);
  useEffect(() => {
    if (deepLinked) setSelectedId(deepLinked);
  }, [deepLinked]);
  const selectedQuery = useOrderPage({
    id: selectedId ?? "",
    page: 1,
    pageSize: 1
  }, !!selectedId);
  const selected = selectedId ? selectedQuery.data?.items.find(o_0 => o_0.id === selectedId) ?? rows.find(o => o.id === selectedId) ?? null : null;
  const setSelected = (order: Order | null) => setSelectedId(order?.id ?? null);
  const closeDetail = () => {
    setSelected(null);
    if (deepLinked) {
      setParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete("order");
        return next;
      }, {
        replace: true
      });
    }
  };

  // Поле поиска пишет в URL с паузой — запрос не на каждую букву.
  const [searchInput, setSearchInput] = useState(f.search ?? "");
  const urlSearch = f.search ?? "";
  useEffect(() => setSearchInput(urlSearch), [urlSearch]);
  const setFilterRef = useRef(table.setFilter);
  setFilterRef.current = table.setFilter;
  useEffect(() => {
    if (searchInput.trim() === urlSearch) return;
    const id = setTimeout(() => setFilterRef.current("search", searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput, urlSearch]);

  // Цифры на чипах считает сервер по статусам в текущем поиске, магазине и
  // датах: чип, обещающий 40 заказов и открывающий 3, — неправда.
  const countOf = (list: readonly string[]) => list.reduce((sum, st) => sum + (counts[st] ?? 0), 0);
  const chips: FilterChip<OrderGroup>[] = (Object.keys(ORDER_GROUPS) as OrderGroup[]).map(key => ({
    value: key,
    label: t(`orders.group.${key}`),
    count: countOf(ORDER_GROUPS[key]),
    tone: GROUP_TONE[key]
  }));
  const allCount = Object.values(counts).reduce((sum_0, n) => sum_0 + n, 0);
  if (error) return <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
				{error}
			</div>;
  if (pageQuery.isPending) return <p className="text-muted-foreground">{t("orders.loading")}</p>;
  return <div className="flex flex-col gap-4">
			<h1 className="text-2xl font-bold">{t("nav.orders")}</h1>

			{/* Chips first, then the narrow controls: the founder reads the shape of
       the queue off the counts before she reaches for a filter. */}
			<FilterChips value={group} chips={chips} onChange={next_0 =>
    // Group and exact status are two grips on the same axis — keeping a
    // stale status while switching group silently empties the table.
    table.setFilters({
      group: next_0,
      status: ""
    })} allLabel={t("orders.group.all")} allCount={allCount} />

			<div className="flex flex-wrap items-center gap-2">
				<Input value={searchInput} onChange={event => setSearchInput(event.target.value)} placeholder={t("orders.searchPlaceholder")} aria-label={t("orders.searchPlaceholder")} className="h-9 w-full sm:max-w-sm" />
				<Select value={status} aria-label={t("orders.filter.anyStatus")} onChange={event_0 => table.setFilter("status", event_0.target.value)}>
					<option value="">{t("orders.filter.anyStatus")}</option>
					{(group ? ORDER_GROUPS[group] : OrderStatus.options).map(value => <option key={value} value={value}>
							{t(`orders.status.${value}`)}
						</option>)}
				</Select>
				<Select value={f.shopId ?? ""} aria-label={t("orders.filter.shop")} data-testid="orders-filter-shop" onChange={event_1 => table.setFilter("shopId", event_1.target.value)}>
					<option value="">{t("orders.filter.anyShop")}</option>
					{shopOptions.map(shop => <option key={shop.id} value={shop.id}>
							{shop.name}
						</option>)}
				</Select>
				<Input type="date" aria-label={t("orders.filter.from")} title={t("orders.filter.from")} className="h-9 w-[9.5rem]" value={f.from ?? ""} max={f.to || undefined} onChange={event_2 => table.setFilter("from", event_2.target.value)} />
				<span className="text-xs text-muted-foreground">—</span>
				<Input type="date" aria-label={t("orders.filter.to")} title={t("orders.filter.to")} className="h-9 w-[9.5rem]" value={f.to ?? ""} min={f.from || undefined} onChange={event_3 => table.setFilter("to", event_3.target.value)} />
				{table.hasFilters && <Button size="sm" variant="ghost" onClick={() => {
        setSearchInput("");
        table.resetFilters();
      }}>
						{t("orders.filter.reset")}
					</Button>}
			</div>

			{total === 0 && !table.hasFilters ? <p className="text-muted-foreground">{t("orders.empty")}</p> : total === 0 ? <p className="text-muted-foreground">{t("orders.searchEmpty")}</p> : <div className="flex flex-col gap-4">
				<Table>
					<TableHeader>
						<TableRow>
							<SortableHead label={t("orders.col.id")} sortKey="id" sort={view.sort} onToggle={view.toggleSort} />
							<SortableHead label={t("orders.col.date")} sortKey="date" sort={view.sort} onToggle={view.toggleSort} />
							<SortableHead label={t("orders.col.customer")} sortKey="customer" sort={view.sort} onToggle={view.toggleSort} />
							<SortableHead label={t("orders.col.shop")} sortKey="shop" sort={view.sort} onToggle={view.toggleSort} />
							<TableHead>{t("orders.col.products")}</TableHead>
							<SortableHead label={t("orders.col.items")} sortKey="items" sort={view.sort} onToggle={view.toggleSort} align="right" className="text-right" />
							<SortableHead label={t("orders.col.total")} sortKey="total" sort={view.sort} onToggle={view.toggleSort} align="right" className="text-right" />
							<SortableHead label={t("orders.col.status")} sortKey="status" sort={view.sort} onToggle={view.toggleSort} />
						</TableRow>
					</TableHeader>
					<TableBody>
						{rows.map(o_1 => <TableRow key={o_1.id} className="cursor-pointer" onClick={() => setSelected(o_1)}>
								<TableCell className="whitespace-nowrap font-medium tabular-nums" title={o_1.id}>
									{orderNo(o_1.id, o_1.number)}
								</TableCell>
								<TableCell className="text-muted-foreground">{formatDateTime(o_1.createdAt)}</TableCell>
								<TableCell className="font-medium">
								{o_1.buyerUserId ? <Link to={`/users/${o_1.buyerUserId}`} className="text-primary hover:underline" onClick={e => e.stopPropagation()}>
										{o_1.contact.name}
									</Link> : o_1.contact.name}
							</TableCell>
								<TableCell className="max-w-[14rem] truncate text-muted-foreground">
									{o_1.groups.map(g => g.shopName).join(", ")}
								</TableCell>
								<TableCell>
									{/* Миниатюры позиций заказа: сразу видно, что заказано,
             без открытия детали. Клик по строке раскрывает состав. */}
									<StackedThumbs items={o_1.groups.flatMap(g_0 => g_0.items.map(it => ({
                photo: it.photo,
                name: it.name
              })))} />
								</TableCell>
								<TableCell className="text-right">{o_1.itemCount}</TableCell>
								<TableCell className="text-right font-medium">{formatPrice(o_1.total)}</TableCell>
								<TableCell>
									<Badge variant={STATUS_VARIANT[o_1.status]}>{t(`orders.status.${o_1.status}`)}</Badge>
								</TableCell>
							</TableRow>)}
					</TableBody>
				</Table>
				<DataTablePagination table={view} />
				</div>}

			<OrderDetail order={selected} onClose={closeDetail} />
		</div>;
}

// Переходы, которые шлюз разрешает площадке (services/orders.ts,
// transitionMatrix). Консоль предлагает только их: кнопка, которая заведомо
// ответит 400, оператору не нужна.
const NEXT_STATUS: Partial<Record<OrderStatus, AdminOrderTargetStatus[]>> = {
  paid: ["confirmed", "cancelled"],
  processing: ["ready_to_ship", "shipped", "cancelled"],
  new: ["confirmed", "cancelled"],
  confirmed: ["in_work", "cancelled"],
  in_work: ["ready_to_ship", "cancelled"],
  ready_to_ship: ["shipped", "in_transit", "cancelled"],
  shipped: ["in_transit", "delivered"],
  in_transit: ["delivered"],
  awaiting_pickup: ["delivered", "cancelled"],
  delivered: ["completed", "return_requested", "dispute_open"],
  completed: ["archived", "return_requested", "dispute_open"],
  return_requested: ["completed", "dispute_open"],
  dispute_open: ["completed"]
};
const PAYMENT_STATUS_KEYS = ["approved", "declined", "pending", "refunded"] as const;
function OrderDetail({
  order,
  onClose
}: {
  order: Order | null;
  onClose: () => void;
}) {
  const {
    t
  } = useTranslation();
  // Ссылка на магазин - по id группы: slug в снимке заказа мог устареть.
  const storefronts = useStorefronts(!!order).data;
  const sellers = useSellers(!!order).data;
  const user = useAuth(state => state.user);
  const canManage = hasAdminPermission(user, "action.orders.manage");
  const {
    data
  } = useOrderShipments(order?.id ?? "");
  const shipments = data ?? [];
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const receipt = useOrderReceipt(order?.id ?? "", showReceipt);
  const [target, setTarget] = useState<AdminOrderTargetStatus | "">("");
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const setStatus = useMutation({
    mutationFn: (input: {
      status: AdminOrderTargetStatus;
      reason?: string;
    }) => api.orders.setStatus(order?.id ?? "", input),
    onSuccess: async () => {
      setTarget("");
      setReason("");
      setConfirmOpen(false);
      setStatusError(null);
      await queryClient.invalidateQueries({
        queryKey: orderKeys.all
      });
    },
    onError: e => {
      setConfirmOpen(false);
      setStatusError(e instanceof Error ? e.message : String(e));
    }
  });
  // Сменили заказ — сбрасываем незавершённое действие прошлого.
  // biome-ignore lint/correctness/useExhaustiveDependencies: реагируем на смену заказа
  useEffect(() => {
    setTarget("");
    setReason("");
    setStatusError(null);
    setShowReceipt(false);
  }, [order?.id]);

  // Заказ ЮЛ по счёту, ожидающий подтверждения оплаты администратором.
  const awaitingInvoice = order?.paymentMethod === "invoice" && order.payment.status !== "approved";
  const confirmInvoice = async () => {
    if (!order) return;
    setConfirming(true);
    try {
      await api.orders.confirmInvoicePayment(order.id);
      await queryClient.invalidateQueries({
        queryKey: orderKeys.all
      });
      onClose();
    } finally {
      setConfirming(false);
    }
  };
  const nextStatuses = order ? NEXT_STATUS[order.status] ?? [] : [];
  const refundAmount = order ? order.payableTotal ?? order.total : 0;
  const paymentStatus = order?.payment.status ?? "";
  const paymentStatusLabel = (PAYMENT_STATUS_KEYS as readonly string[]).includes(paymentStatus) ? t(`orders.paymentStatus.${paymentStatus}`) : paymentStatus;
  const history = [...(order?.statusHistory ?? [])].reverse();
  return <Dialog open={order !== null} onOpenChange={open => !open && onClose()}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
				{order && <>
						<DialogHeader>
							<DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
								<span className="tabular-nums" title={order.id}>
									{t("orders.detail.title", {
                number: orderNo(order.id, order.number)
              })}
								</span>
								<Badge variant={STATUS_VARIANT[order.status]}>
									{t(`orders.status.${order.status}`)}
								</Badge>
								<span className="text-xs font-normal text-muted-foreground">
									{formatDateTime(order.createdAt)}
								</span>
							</DialogTitle>
						</DialogHeader>

						<div className="flex min-w-0 flex-col gap-5 text-sm">
							{/* customer + shipping */}
							<div className="grid gap-4 sm:grid-cols-2">
								<Field label={t("orders.detail.customer")}>
									<p>
										{order.buyerUserId ? <Link to={`/users/${order.buyerUserId}`} className="text-primary hover:underline">
												{order.contact.name}
											</Link> : order.contact.name}
									</p>
									{order.contact.email && <a href={`mailto:${order.contact.email}`} className="block text-muted-foreground hover:underline">
											{order.contact.email}
										</a>}
									{order.contact.phone && <a href={`tel:${order.contact.phone.replace(/[^+\d]/g, "")}`} className="block text-muted-foreground hover:underline">
											{order.contact.phone}
										</a>}
								</Field>
								<Field label={t("orders.detail.shipping")}>
									<p>{order.address.country}, {order.address.city}</p>
									<p className="text-muted-foreground">{order.address.street}</p>
									<p className="text-muted-foreground">{order.address.postalCode}</p>
									{order.address.comment && <p className="text-muted-foreground italic">{order.address.comment}</p>}
								</Field>
								<Field label={t("orders.detail.delivery")}>
									<p>
										{/* Самовывоз от продавца — свой способ, а не «Самовывоз» из ПВЗ перевозчика. */}
										{order.groups.length > 0 && order.groups.every(g => g.shipment?.carrier === "seller_pickup") ? t("orders.detail.carrier.seller_pickup") : t(`orders.delivery.${order.delivery}`)}
									</p>
									{order.trackingNumber && <p className="text-muted-foreground">
											{t("orders.detail.tracking")}: <span className="font-mono">{order.trackingNumber}</span>
										</p>}
								</Field>
								<Field label={t("orders.detail.payment")}>
									<p className="capitalize">
										{order.paymentMethod === "invoice" ? t("orders.detail.invoice", {
                  defaultValue: "Оплата по счёту"
                }) : `${order.payment.brand} ···· ${order.payment.last4}`}
									</p>
									<p className="text-muted-foreground">{paymentStatusLabel}</p>
									{order.payment.transactionId && <p className="break-all font-mono text-xs text-muted-foreground">{order.payment.transactionId}</p>}
									{typeof order.balanceApplied === "number" && order.balanceApplied > 0 && <p className="text-muted-foreground">
											{t("orders.detail.balanceApplied", {
                  defaultValue: "Списано с баланса"
                })}:{" "}
											{formatPrice(order.balanceApplied)}
										</p>}
									{awaitingInvoice && canManage && <Button size="sm" className="mt-2" disabled={confirming} onClick={confirmInvoice}>
											{t("orders.detail.confirmInvoice", {
                  defaultValue: "Подтвердить оплату по счёту"
                })}
										</Button>}
								</Field>
							</div>

							{/* Деньги, которые площадка вернула покупателю при отмене. */}
							{order.refund && <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3" data-testid="order-refund">
									<p className="font-medium">
										{t(`orders.refund.${order.refund.status}`)}
										{typeof order.refund.amount === "number" ? ` · ${formatPrice(order.refund.amount)}` : ""}
									</p>
									<p className="text-xs text-muted-foreground">
										{formatDateTime(order.refund.createdAt)}
										{order.refund.transactionId ? ` · ${order.refund.transactionId}` : ""}
										{order.refund.reason ? ` · «${order.refund.reason}»` : ""}
									</p>
								</div>}
							{/* fd-order-18: возврат через API Робокассы — состояние и повтор. */}
							<OrderPaymentRefunds orderId={order.id} canManage={canManage} />

							{/* items by shop */}
							<div className="flex flex-col gap-4">
								{order.groups.map(g_0 => <div key={g_0.shopId} className="rounded-md border p-3">
										<div className="mb-2 flex items-center justify-between gap-2">
											<Link to={shopRoute(storefronts, sellers, g_0.shopId).href} className="font-semibold text-primary hover:underline">
												{g_0.shopName}
											</Link>
											<span className="text-xs text-muted-foreground">{t("orders.detail.seller")}</span>
										</div>
										<div className="flex flex-col gap-2">
											{g_0.items.map(it => <div key={`${it.productId}-${it.variantId ?? it.size}`} className="flex items-center gap-3">
													<div className="size-10 shrink-0 overflow-hidden rounded bg-muted">
														{it.photo && <img src={it.photo} alt="" className="size-full object-cover" />}
													</div>
													<div className="min-w-0 flex-1">
														<Link to={`/products/${it.productId}`} className="block truncate hover:underline">
															{it.name}
														</Link>
														<p className="text-xs text-muted-foreground">
															{it.qty} × {formatPrice(it.unitPrice)}
															{it.oldUnitPrice && it.oldUnitPrice > it.unitPrice ? ` (${formatPrice(it.oldUnitPrice)})` : ""}
													{it.variantLabel ? ` · ${it.variantLabel}` : it.size ? ` · ${it.size}` : ""}
														</p>
														{/* Выбранные покупателем изменения: гейтвей кладёт их
                  первыми строками характеристик. Без вывода поддержка
                  разбирает спор по заказу вслепую. */}
														{it.specs && it.specs.length > 0 && <p className="text-xs text-muted-foreground">
																{it.specs.map(s => `${s.label}: ${s.value}`).join(" · ")}
															</p>}
														{it.kind === "customized" && <Badge variant="outline" className="mt-1">
																{t("orders.itemCustomized", {
                        defaultValue: "Индивидуальное изготовление"
                      })}
															</Badge>}
													</div>
													<span className="font-medium">{formatPrice(it.lineTotal)}</span>
												</div>)}
										</div>
										{g_0.shipment?.carrier === "seller_pickup" &&
              // Самовывоз от продавца: способ и точка выдачи из снимка заказа.
              <div className="mt-2 rounded-md bg-muted/50 p-2 text-xs" data-testid="order-seller-pickup">
												<p className="font-medium">{t("orders.detail.carrier.seller_pickup")}</p>
												{g_0.shipment.pickupPoint ? <>
														<p>
															{g_0.shipment.pickupPoint.name}:{" "}
															{[g_0.shipment.pickupPoint.city, g_0.shipment.pickupPoint.address].filter(Boolean).join(", ")}
														</p>
														<p className="text-muted-foreground">
															{[g_0.shipment.pickupPoint.schedule, g_0.shipment.pickupPoint.phone, t("orders.detail.sellerPickup.terms", {
                      ready: g_0.shipment.pickupPoint.readyLabel,
                      days: g_0.shipment.pickupPoint.storageDays
                    })].filter(Boolean).join(" · ")}
														</p>
													</> : g_0.shipment.pvzAddress && <p>{g_0.shipment.pvzAddress}</p>}
											</div>}
										{g_0.shipment && g_0.shipment.carrier !== "seller_pickup" && <p className="mt-2 text-xs text-muted-foreground">
												{g_0.shipment.tariffName ?? (g_0.shipment.method ? t(`orders.delivery.${g_0.shipment.method}`) : "")}
												{g_0.shipment.deliveryDays ? ` · ${g_0.shipment.deliveryDays}` : ""}
												{g_0.shipment.pvzAddress ? ` · ${g_0.shipment.pvzAddress}` : ""}
											</p>}
										<div className="mt-2 flex justify-between border-t pt-2 text-xs text-muted-foreground">
											<span>{t("orders.detail.deliveryFee")}</span>
											<span>{g_0.deliveryFee === 0 ? t("orders.detail.free") : formatPrice(g_0.deliveryFee)}</span>
										</div>
									</div>)}
							</div>

							{/* carrier shipments (СДЭК); flat-rate orders have none */}
							{shipments.length > 0 && <div className="flex flex-col gap-2">
									<p className="text-xs font-medium uppercase text-muted-foreground">
										{t("orders.detail.shipments")}
									</p>
									{shipments.map(s_0 => <div key={s_0.id} className="rounded-md border p-3 text-sm">
											<div className="flex items-center justify-between gap-2">
												<span className="font-medium">
													{t(`orders.detail.carrier.${s_0.carrier}`, {
                    defaultValue: s_0.carrier
                  })}
												</span>
												{s_0.carrier !== "seller_pickup" && <span className="font-mono text-xs">
														{s_0.cdekNumber ?? t("orders.detail.awaitingNumber")}
													</span>}
											</div>
											<div className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
												{s_0.statusName && <span>{s_0.statusName}</span>}
												{/* У самовывоза «состояние регистрации у перевозчика» не бывает — только статус выдачи. */}
												{s_0.carrier !== "seller_pickup" && <span>
														{t("orders.detail.state")}: {t(`orders.shipmentState.${s_0.state}`, {
                    defaultValue: s_0.state
                  })}
													</span>}
											</div>
										</div>)}
								</div>}

							{/* totals */}
							<div className="flex flex-col gap-1 border-t pt-3">
								<Row label={t("orders.detail.subtotal")} value={formatPrice(order.subtotal)} />
								{typeof order.discount === "number" && order.discount > 0 && <Row label={t("orders.detail.discount")} value={`−${formatPrice(order.discount)}`} />}
								<Row label={t("orders.detail.deliveryTotal")} value={formatPrice(order.deliveryTotal)} />
								<div className="flex justify-between pt-1 text-base font-bold">
									<span>{t("orders.detail.total")}</span>
									<span>{formatPrice(order.total)}</span>
								</div>
							</div>

							{/* Чек (54-ФЗ): то, что уходит в онлайн-кассу вместе с платежом. */}
							<div className="flex flex-col gap-2">
								<Button variant="outline" size="sm" className="self-start" onClick={() => setShowReceipt(v => !v)} data-testid="order-receipt-toggle">
									{showReceipt ? t("orders.receipt.hide") : t("orders.receipt.show")}
								</Button>
								{showReceipt && <div className="rounded-md border p-3 text-xs" data-testid="order-receipt">
										{receipt.error ? <p className="text-destructive">{(receipt.error as Error).message}</p> : !receipt.data ? <p className="text-muted-foreground">{t("orders.receipt.loading")}</p> : <>
												<p className="mb-2 text-muted-foreground">{t("orders.receipt.note")}</p>
												<table className="w-full">
													<tbody>
														{receipt.data.Items.map((line, i) => <tr key={`${line.label}-${i}`} className="border-t first:border-t-0">
																<td className="py-1 pr-2">{line.label}</td>
																<td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">
																	{line.quantity} × {formatPrice(line.price)}
																</td>
																<td className="py-1 text-right font-medium tabular-nums">{formatPrice(line.amount)}</td>
															</tr>)}
													</tbody>
												</table>
												<p className="mt-2 text-muted-foreground">
													{t("orders.receipt.sentTo")}: {[receipt.data.email, receipt.data.phone].filter(Boolean).join(", ") || "—"}
												</p>
											</>}
									</div>}
							</div>

							{/* Заявки на возврат по заказу — переход в раздел «Возвраты». */}
							{order.returnRequests && order.returnRequests.length > 0 && <Field label={t("orders.detail.returns")}>
									<div className="flex flex-col gap-1">
										{order.returnRequests.map(r => <Link key={r.id} to={`/returns?return=${r.id}`} className="text-primary hover:underline">
												{t(`returns.kind.${r.kind}`)} · {t(`returns.status.${r.status}`, {
                  defaultValue: r.status
                })} · {formatDateTime(r.createdAt)}
											</Link>)}
									</div>
								</Field>}

							{/* История статусов: кто и когда двигал заказ. */}
							<Field label={t("orders.detail.history")}>
								{history.length === 0 ? <p className="text-muted-foreground">{t("orders.detail.historyEmpty")}</p> : <ol className="flex flex-col gap-1.5" data-testid="order-history">
										{history.map(e_0 => <li key={e_0.id} className="flex flex-wrap items-baseline gap-x-2 text-xs">
												<span className="whitespace-nowrap text-muted-foreground">{formatDateTime(e_0.createdAt)}</span>
												<span className="font-medium">
													{t(`orders.status.${e_0.newStatus}`, {
                    defaultValue: e_0.newStatus
                  })}
												</span>
												<span className="text-muted-foreground">· {t(`orders.actor.${e_0.actorType}`)}</span>
												{e_0.reason && <span className="text-muted-foreground">· {t(`orders.reason.${e_0.reason}`, {
                    defaultValue: e_0.reason
                  })}</span>}
											</li>)}
									</ol>}
							</Field>

							{/* Действия площадки со статусом. Отмена — с возвратом денег. */}
							{canManage && nextStatuses.length > 0 && <div className="flex flex-col gap-2 rounded-md border p-3" data-testid="order-status-actions">
									<p className="text-xs font-medium uppercase text-muted-foreground">{t("orders.action.title")}</p>
									<div className="flex flex-wrap items-center gap-2">
										<Select value={target} onChange={event => setTarget(event.target.value as AdminOrderTargetStatus | "")} aria-label={t("orders.action.target")}>
											<option value="">{t("orders.action.choose")}</option>
											{nextStatuses.map(value => <option key={value} value={value}>
													{t(`orders.status.${value}`)}
												</option>)}
										</Select>
										<Input value={reason} onChange={event_0 => setReason(event_0.target.value)} placeholder={target === "cancelled" ? t("orders.action.reasonRequired") : t("orders.action.reason")} className="h-9 min-w-0 flex-1" />
										<Button size="sm" variant={target === "cancelled" ? "destructive" : "default"} disabled={!target || setStatus.isPending || target === "cancelled" && !reason.trim()} onClick={() => setConfirmOpen(true)}>
											{t("orders.action.apply")}
										</Button>
									</div>
									{target === "cancelled" && <p className="text-xs text-muted-foreground">
											{t("orders.action.cancelHint", {
                amount: formatPrice(refundAmount)
              })}
										</p>}
									{statusError && <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
											{statusError}
										</p>}
								</div>}
						</div>
						<ConfirmDialog open={confirmOpen} title={target === "cancelled" ? t("orders.action.cancelTitle", {
          id: orderNo(order.id, order.number)
        }) : t("orders.action.confirmTitle", {
          status: target ? t(`orders.status.${target}`) : ""
        })} description={target === "cancelled" ? t("orders.action.cancelDescription", {
          amount: formatPrice(refundAmount)
        }) : t("orders.action.confirmDescription")} confirmVariant={target === "cancelled" ? "destructive" : "default"} busy={setStatus.isPending} onConfirm={() => {
          if (!target) return;
          setStatus.mutate({
            status: target,
            reason: reason.trim() || undefined
          });
        }} onClose={() => setConfirmOpen(false)} />
					</>}
			</DialogContent>
		</Dialog>;
}
function Field(t0) {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div>{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function Row(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span>{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="text-foreground">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex justify-between text-muted-foreground">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}